import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { $api } from '../api/queryClient'
import { useLiveSubscription, useScopeLive } from '../contexts/LiveEventsContext'
import type { Permission } from '../api/schema'
import { holdsPermission, usePermissions } from '../contexts/PermissionsContext'
import type { AttentionCounts } from '../lib/attention'

const POLL_MS = 60_000

/**
 * What waits on the caller (spec 2026-10-27-23), for the sidebar badges and
 * the tab title. One query, refreshed by the live socket on issues,
 * conversations, documents and contracts (live-invalidation.ts maps their
 * hints to this path), and polled every minute while any of them is not live.
 * Enabled once the membership is known, like every sidebar count.
 */
export function useAttentionCounts(org: string, enabled = true): AttentionCounts | undefined {
    const { permissions, membershipKnown } = usePermissions()
    const can = (p: Permission) => enabled && membershipKnown && holdsPermission(permissions, p)
    const issues = can('issues.read')
    const conversations = can('conversations.read')
    const documents = can('documents.read')
    const contracts = can('contracts.read')

    useLiveSubscription({ entity: 'issues' }, { enabled: issues })
    useLiveSubscription({ entity: 'conversations' }, { enabled: conversations })
    useLiveSubscription({ entity: 'documents' }, { enabled: documents })
    useLiveSubscription({ entity: 'contracts' }, { enabled: contracts })
    const live = [
        useScopeLive({ entity: 'issues' }) || !issues,
        useScopeLive({ entity: 'conversations' }) || !conversations,
        useScopeLive({ entity: 'documents' }) || !documents,
        useScopeLive({ entity: 'contracts' }) || !contracts,
    ].every(Boolean)

    const { data } = $api.useQuery(
        'get',
        '/api/v1/organizations/{orgCode}/users/me/attention',
        { params: { path: { orgCode: org } } },
        {
            enabled: enabled && membershipKnown && !!org,
            refetchInterval: live ? false : POLL_MS,
            staleTime: 30_000,
        },
    )

    return data as AttentionCounts | undefined
}

const ATTENTION_PATH = '/api/v1/organizations/{orgCode}/users/me/attention'

/**
 * Stamps the caller's ticket "last seen" marker (server clock) and refreshes
 * the badges with the counts it returns. The steering page calls it once per
 * visit, after its first list response.
 */
export function useMarkIssuesSeen(org: string) {
    const queryClient = useQueryClient()
    const mutation = $api.useMutation('post', '/api/v1/organizations/{orgCode}/users/me/attention/issues-seen', {
        onSuccess: () => {
            void queryClient.invalidateQueries({
                predicate: (query) => {
                    const key = query.queryKey as unknown[]
                    return key[0] === 'get' && key[1] === ATTENTION_PATH
                },
            })
        },
    })
    return useCallback(() => {
        mutation.mutate({ params: { path: { orgCode: org } } })
        // mutation identity changes every render; the org is what matters.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [org])
}
